import React, { useMemo, useState } from "react";
import { Box, Typography, TextField } from "@mui/material";
import ModalContent from "src/Shared/Components/ModalContent/ModalContent";
import { useModal, useStore } from "src/Shared/Hooks";
import { fields } from "./fields";
import { getFormValidation } from "src/Shared/Components/FormValidations/MobxFormValidation";
import { SLA_CONFIG } from "../SLAManagement.config";
import { useStyles } from "../SLAManagement.styles";
import { finalize } from "rxjs";
import { IResult } from "src/Shared/Interfaces/IResult";
import { ToastService } from "src/Shared/Services";
import { AxiosError } from "axios";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { EDITOR_TYPES } from "src/Shared/Enums/EditorType.enum";
import moment from "moment";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";

type Props = {
  CompanyRowID: string;
  onSaved?: () => void;
  onSyncPriceReviewDate?: (expiryDate: string | null) => void;
};

const ReviewPriceComment: React.FC<Props> = ({ CompanyRowID, onSaved, onSyncPriceReviewDate }) => {
  const {
    rootStore: { companyStore, uiStore },
  } = useStore();

  const priceReviewForm = useMemo(() => getFormValidation(fields), []);
  const getField = (key: string) => priceReviewForm.$(key);

  const classes = useStyles();

  const { close } = useModal();

  const handleSubmit = () => {

    if (!priceReviewForm.isValid) {
      priceReviewForm.showErrors();
      return;
    }

    const payload = {
      comment: getField("comment").value,
      companyRowID: CompanyRowID,
      priceReviewDate: DateTimeUtils.toApiDate(getField("priceExpiryReviewDate").value),
    };

    uiStore.setGlobalLoader(true);
    companyStore.savePriceReviewComment(payload)
      .pipe(
        finalize(() => {
          uiStore.setGlobalLoader(false);
        })
      )
      .subscribe({
        next: (data: string) => {
          ToastService.success(data);
          onSyncPriceReviewDate(getField("priceExpiryReviewDate").value);
          close();
          if (typeof onSaved === "function") onSaved();
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  const handleCancel = () => {
    getField("comment").value = "";
    getField("priceExpiryReviewDate").value = "";
    close();
  };
  
  const detailGridProps: IGridFormControlProps = {
    row: 1,
    col: 2,
    spacing: 2,
    itemResponsive: {
      lg: 12,
      md: 12,
      sm: 12,
    },
    inputControls: [
      {
        field: getField("comment"),
        fieldKey: "comment",
        type: EDITOR_TYPES.TEXT_FIELD,
        multiline: true,
        rows: 4,
      },
      {
        field: getField("priceExpiryReviewDate"),
        fieldKey: "priceExpiryReviewDate",
        type: EDITOR_TYPES.DATE,
        minDate: moment(new Date())
      },
    ],
    form: priceReviewForm,
  };

  return (
    <ModalContent
      title={"Record price review"}
      buttonText={"Save price review"}
      onSaveButtonClick={handleSubmit}
      cancelButton
      onCancel={handleCancel}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box className={`${classes.alertBlue} ${classes.statusBar}`}>
          <Typography className={classes.statusBarText}>
            This will be written to the SLA comments with a [Price Review] tag
            and a timestamp.
          </Typography>
        </Box>
        <GridFormControl {...detailGridProps} />
      </Box>
    </ModalContent>
  );
};

export default ReviewPriceComment;
